import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { serialApi, SerialItem, SerialMovement } from '@/api/serial.api';
import AppLayout from '@/components/feature/AppLayout';

export default function SerialDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [serial, setSerial] = useState<SerialItem | null>(null);
  const [movements, setMovements] = useState<SerialMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Status Change Modal State
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newCondition, setNewCondition] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);

  useEffect(() => {
    if (id) {
      loadDetail(id);
    }
  }, [id]);

  const loadDetail = async (serialId: string) => {
    try {
      setLoading(true);
      setError(null);
      const res = await serialApi.getById(serialId);
      if (res.success) {
        setSerial(res.serial);
        setMovements(res.movements || []);
        setNewStatus(res.serial.status);
        setNewCondition(res.serial.condition);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load serial details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatusSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serial) return;
    try {
      setSavingStatus(true);
      await serialApi.updateStatus(serial.id, {
        status: newStatus,
        condition: newCondition,
        notes: statusNotes,
      });
      setStatusModalOpen(false);
      setStatusNotes('');
      loadDetail(serial.id);
    } catch (err: any) {
      alert(`Error updating status: ${err.message}`);
    } finally {
      setSavingStatus(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="p-12 text-center text-gray-500">
          <i className="ri-loader-4-line text-3xl animate-spin text-blue-600 block mb-2"></i>
          Loading serial details...
        </div>
      </AppLayout>
    );
  }

  if (error || !serial) {
    return (
      <AppLayout>
        <div className="p-8 max-w-2xl mx-auto text-center space-y-4">
          <div className="p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-2xl text-sm">
            {error || 'Serial record not found.'}
          </div>
          <button
            onClick={() => navigate('/inventory/serials')}
            className="px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-semibold cursor-pointer"
          >
            Back to Serials List
          </button>
        </div>
      </AppLayout>
    );
  }

  const purchaseRate = Number(serial.purchase_rate || 0);
  const saleRate = Number(serial.sale_rate || 0);
  const profitMargin = saleRate > 0 ? saleRate - purchaseRate : null;

  return (
    <AppLayout>
      <div className="p-6 max-w-6xl mx-auto space-y-6 animate-in fade-in duration-300">
      {/* Top Breadcrumb & Action */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <button
          onClick={() => navigate('/inventory/serials')}
          className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1 transition-colors"
        >
          <i className="ri-arrow-left-line"></i> Back to Serial Numbers
        </button>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setStatusModalOpen(true)}
            className="px-4 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-all"
          >
            <i className="ri-settings-4-line text-blue-600"></i> Update Status / Condition
          </button>
          <button
            type="button"
            onClick={() => navigate(`/inventory/serial-tracking?sn=${encodeURIComponent(serial.serial_number)}`)}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-all"
          >
            <i className="ri-route-line"></i> Trace Journey
          </button>
        </div>
      </div>

      {/* Main Header Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="p-2.5 bg-blue-50 text-blue-600 rounded-xl text-2xl font-mono">
              <i className="ri-barcode-box-line"></i>
            </span>
            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-2xl font-bold font-mono text-gray-900">{serial.serial_number}</h1>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
                  {serial.status}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                  Condition: {serial.condition}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Vendor Tag: <span className="font-mono font-medium text-gray-800">{serial.vendor_serial_number || 'None'}</span>
                {serial.imei ? ` • IMEI: ${serial.imei}` : ''}
              </p>
            </div>
          </div>
        </div>

        <div className="text-right flex flex-col items-start md:items-end">
          <span className="text-xs text-gray-400">Current Warehouse</span>
          <span className="text-sm font-semibold text-gray-900">{serial.warehouse_name || 'Main Warehouse'}</span>
          {serial.shelf_location && (
            <span className="text-xs text-gray-500">Shelf: {serial.shelf_location}</span>
          )}
        </div>
      </div>

      {/* Quick Action Banner for RMA / Return / Repair */}
      {(serial.status === 'RETURNED' || serial.status === 'IN_REPAIR') && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-amber-500 text-white rounded-xl">
              <i className="ri-tools-line text-lg" />
            </span>
            <div>
              <h4 className="text-sm font-bold text-amber-900">
                {serial.status === 'RETURNED' ? 'RMA Return Processing' : 'Active Repair Unit'}
              </h4>
              <p className="text-xs text-amber-700">
                {serial.status === 'RETURNED'
                  ? 'Unit returned by customer. Decide whether to repair, scrap, or accept back into sellable stock.'
                  : 'Unit is currently under repair. Mark as restored to stock once repaired or scrap if unrepairable.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {serial.status === 'RETURNED' && (
              <>
                <button
                  type="button"
                  onClick={async () => {
                    const notes = prompt('Enter repair service center or issue details:', 'Sent to Service Center');
                    if (notes === null) return;
                    await serialApi.updateStatus(serial.id, { status: 'IN_REPAIR', condition: serial.condition, notes });
                    loadDetail(serial.id);
                  }}
                  className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer"
                >
                  <i className="ri-tools-fill" /> Send to Repair
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const notes = prompt('Reason for scrap:', 'Beyond economical repair');
                    if (notes === null) return;
                    await serialApi.updateStatus(serial.id, { status: 'SCRAPPED', condition: 'DEFECTIVE', notes });
                    loadDetail(serial.id);
                  }}
                  className="px-3.5 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line" /> Scrap
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const notes = prompt('Condition check notes:', 'Unit inspected - fully functional');
                    if (notes === null) return;
                    await serialApi.updateStatus(serial.id, { status: 'IN_STOCK', condition: 'GOOD', notes });
                    loadDetail(serial.id);
                  }}
                  className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer"
                >
                  <i className="ri-check-line" /> Accept Back
                </button>
              </>
            )}
            {serial.status === 'IN_REPAIR' && (
              <>
                <button
                  type="button"
                  onClick={async () => {
                    const notes = prompt('Repair completion notes:', 'Repaired, tested and functional');
                    if (notes === null) return;
                    await serialApi.updateStatus(serial.id, { status: 'IN_STOCK', condition: 'GOOD', notes });
                    loadDetail(serial.id);
                  }}
                  className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer"
                >
                  <i className="ri-checkbox-circle-line" /> Repaired → IN_STOCK
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const notes = prompt('Reason for scrapping after repair attempt:', 'Cannot be repaired');
                    if (notes === null) return;
                    await serialApi.updateStatus(serial.id, { status: 'SCRAPPED', condition: 'DEFECTIVE', notes });
                    loadDetail(serial.id);
                  }}
                  className="px-3.5 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer"
                >
                  <i className="ri-close-circle-line" /> Scrap
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* 3-Column Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Card 1: Item Details */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-3">
          <div className="flex items-center gap-2 text-blue-600 border-b border-gray-100 pb-2.5">
            <i className="ri-archive-line text-lg"></i>
            <h3 className="text-sm font-bold text-gray-900">Item Details</h3>
          </div>
          <div className="space-y-2 text-xs">
            <div>
              <span className="text-gray-400 block">Item Name</span>
              <span className="font-semibold text-gray-800 text-sm">{serial.item_name}</span>
            </div>
            {serial.item_code && (
              <div>
                <span className="text-gray-400 block">Item Code / SKU</span>
                <span className="font-mono text-gray-700">{serial.item_code}</span>
              </div>
            )}
            {serial.batch_number && (
              <div>
                <span className="text-gray-400 block">Batch (Dual Tracked)</span>
                <span className="font-mono font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                  {serial.batch_number}
                </span>
              </div>
            )}
            <div>
              <span className="text-gray-400 block">Warranty Expiry</span>
              <span className="font-medium text-gray-800">{serial.warranty_expiry_date || 'Not specified'}</span>
            </div>
          </div>
        </div>

        {/* Card 2: Purchase Inward */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-3">
          <div className="flex items-center gap-2 text-emerald-600 border-b border-gray-100 pb-2.5">
            <i className="ri-inbox-archive-line text-lg"></i>
            <h3 className="text-sm font-bold text-gray-900">Purchase Inward</h3>
          </div>
          <div className="space-y-2 text-xs">
            <div>
              <span className="text-gray-400 block">GRN Number</span>
              <span className="font-mono font-bold text-emerald-700">{serial.grn_number || 'Direct Inward'}</span>
            </div>
            <div>
              <span className="text-gray-400 block">Inward Date</span>
              <span className="font-medium text-gray-700">{serial.grn_date || '—'}</span>
            </div>
            {serial.supplier_name && (
              <div>
                <span className="text-gray-400 block">Supplier</span>
                <span className="font-medium text-gray-800">{serial.supplier_name}</span>
              </div>
            )}
            {serial.po_number && (
              <div>
                <span className="text-gray-400 block">Purchase Order</span>
                <span className="font-mono text-gray-700">{serial.po_number}</span>
              </div>
            )}
            <div>
              <span className="text-gray-400 block">Purchase Cost</span>
              <span className="font-bold text-gray-900 text-sm">₹{purchaseRate.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>

        {/* Card 3: Sale Outward */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-3">
          <div className="flex items-center gap-2 text-indigo-600 border-b border-gray-100 pb-2.5">
            <i className="ri-shopping-cart-2-line text-lg"></i>
            <h3 className="text-sm font-bold text-gray-900">Sale Details</h3>
          </div>
          {serial.status === 'SOLD' || serial.invoice_number ? (
            <div className="space-y-2 text-xs">
              <div>
                <span className="text-gray-400 block">Invoice Number</span>
                <span className="font-mono font-bold text-indigo-700">{serial.invoice_number}</span>
              </div>
              <div>
                <span className="text-gray-400 block">Invoice Date</span>
                <span className="font-medium text-gray-700">{serial.invoice_date || '—'}</span>
              </div>
              <div>
                <span className="text-gray-400 block">Customer</span>
                <span className="font-medium text-gray-800">{serial.customer_name || 'Customer'}</span>
              </div>
              <div>
                <span className="text-gray-400 block">Sale Rate</span>
                <span className="font-bold text-gray-900 text-sm">₹{saleRate.toLocaleString('en-IN')}</span>
              </div>
              {profitMargin !== null && (
                <div>
                  <span className="text-gray-400 block">Gross Profit</span>
                  <span className={`font-bold ${profitMargin >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    ₹{profitMargin.toLocaleString('en-IN')}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="py-6 text-center text-gray-400 text-xs">
              <i className="ri-time-line text-2xl block mb-1 opacity-50"></i>
              Item not sold yet. Currently in warehouse storage.
            </div>
          )}
        </div>
      </div>

      {/* Movement History Timeline */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
          <div className="flex items-center gap-2 text-gray-900">
            <span className="p-1.5 bg-gray-100 rounded-lg text-gray-600">
              <i className="ri-history-line"></i>
            </span>
            <h2 className="text-base font-bold">Complete Movement & Audit Trail</h2>
          </div>
          <span className="text-xs text-gray-400">{movements.length} logged events</span>
        </div>

        {movements.length === 0 ? (
          <div className="py-8 text-center text-gray-400 text-xs">
            No movements recorded for this serial number.
          </div>
        ) : (
          <div className="relative pl-6 space-y-6 before:content-[''] before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-200">
            {movements.map((m) => {
              let dotColor = 'bg-blue-600';
              let icon = 'ri-record-circle-line';
              if (m.movement_type === 'IN') {
                dotColor = 'bg-emerald-500';
                icon = 'ri-login-circle-line';
              } else if (m.movement_type === 'OUT') {
                dotColor = 'bg-indigo-600';
                icon = 'ri-logout-circle-line';
              } else if (m.movement_type === 'RETURN') {
                dotColor = 'bg-amber-500';
                icon = 'ri-arrow-go-back-line';
              } else if (m.movement_type === 'TRANSFER') {
                dotColor = 'bg-purple-600';
                icon = 'ri-swap-line';
              }

              return (
                <div key={m.id} className="relative group">
                  <div className={`absolute -left-6 top-1 w-5 h-5 rounded-full ${dotColor} text-white flex items-center justify-center text-xs shadow-sm`}>
                    <i className={icon}></i>
                  </div>
                  <div className="bg-gray-50 group-hover:bg-blue-50/40 p-4 rounded-xl border border-gray-200/70 transition-colors">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-900 font-mono">
                          [{m.movement_type}] {m.reference_type}
                        </span>
                        {m.reference_no && (
                          <span className="text-xs font-medium text-gray-600 bg-white px-2 py-0.5 rounded border border-gray-200">
                            {m.reference_no}
                          </span>
                        )}
                        {m.to_status && (
                          <span className="text-[10px] font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                            → {m.to_status}
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-gray-400 font-mono">
                        {new Date(m.created_at).toLocaleString()}
                      </span>
                    </div>

                    {m.notes && (
                      <p className="text-xs text-gray-600 mt-2 font-sans">
                        {m.notes}
                      </p>
                    )}

                    <div className="flex items-center gap-4 mt-2 pt-2 border-t border-gray-200/50 text-[11px] text-gray-500">
                      {m.party_name && (
                        <span>
                          <strong className="text-gray-700">Party:</strong> {m.party_name}
                        </span>
                      )}
                      {m.to_warehouse_name && (
                        <span>
                          <strong className="text-gray-700">Location:</strong> {m.to_warehouse_name}
                        </span>
                      )}
                      {m.done_by_name && (
                        <span>
                          <strong className="text-gray-700">By:</strong> {m.done_by_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* UPDATE STATUS MODAL */}
      {statusModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <form
            onSubmit={handleUpdateStatusSubmit}
            className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-md w-full p-6 space-y-4"
          >
            <div className="flex justify-between items-center">
              <h3 className="text-base font-bold text-gray-900">Update Serial Status & Condition</h3>
              <button
                type="button"
                onClick={() => setStatusModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="IN_STOCK">IN_STOCK (Warehouse Available)</option>
                  <option value="DEFECTIVE">DEFECTIVE (Faulty unit)</option>
                  <option value="IN_REPAIR">IN_REPAIR (At service center)</option>
                  <option value="ON_HOLD">ON_HOLD (Blocked)</option>
                  <option value="SCRAPPED">SCRAPPED (Written off)</option>
                  <option value="DEMO">DEMO (Non-saleable demo)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Condition</label>
                <select
                  value={newCondition}
                  onChange={(e) => setNewCondition(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="GOOD">GOOD</option>
                  <option value="DAMAGED_BOX">DAMAGED BOX</option>
                  <option value="DEFECTIVE">DEFECTIVE</option>
                  <option value="REFURBISHED">REFURBISHED</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Audit Notes / Reason</label>
                <textarea
                  rows={3}
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Explain reason for status adjustment..."
                  className="w-full p-2.5 text-xs rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setStatusModalOpen(false)}
                className="px-4 py-2 text-xs border border-gray-200 rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingStatus}
                className="px-4 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold disabled:opacity-50"
              >
                {savingStatus ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
    </AppLayout>
  );
}
